"use client";

/**
 * PipelineLoop — the 4-stage Compass cycle, replayed as a loop.
 *
 * React reimplementation of
 * `docs/Fable UiUx review/compass-pipeline-prototype-v2.html`:
 *
 *   Stage 1 ANALYZE     7 analyzer cards light up in a stagger; each card's
 *                       micro-SVG (bars / curve draw-in / gauge needle /
 *                       volume blips / calendar) animates, then reveals its
 *                       signal glyph + self-confidence.
 *   Stage 2 CHALLENGE   the red-team stamps every analyzer ok (✓) or
 *                       flagged (⚑) one by one.
 *   Stage 3 DEBATE      five persona seats around a boardroom table light
 *                       up round by round; conviction rings fill toward
 *                       each persona's final stance; four transcript lines
 *                       surface; the judge verdict lands.
 *   Stage 4 SYNTHESIZE  the writer core spins, then the brief card slides
 *                       in with a conviction ring counting to 7.0.
 *
 * Architecture: the entire render is a PURE function of the StageFrame
 * from `useStageDriver` — no setTimeout timeline anywhere. The driver
 * gives hover-pause, off-viewport pause, clickable stage markers (the
 * stage rail headers are buttons), and reduced-motion pinning to the
 * completed end state (full grid lit, verdict shown, brief sent).
 *
 * The numbers/copy in this loop are the approved MSFT sample-brief
 * narrative — illustrative of one real cycle shape, with the synthesis
 * line verbatim from the approved sample. No buy/sell/hold language.
 */

import { useMemo } from "react";
import { useStageDriver } from "@/lib/motion";
import { cn } from "@/lib/utils/cn";
import {
  MONO,
  SERIF,
  EASE,
  LINE,
  LINE_STRONG,
  clamp01,
  easeOutCubic,
  lerp,
  stageLocalProgress,
  window01,
} from "./shared";

// ─── Timeline ────────────────────────────────────────────────────────────

/** Full-fat default: ~24s/loop (the /compass treatment). */
const DEFAULT_DURATIONS: [number, number, number, number] = [
  6500, 3500, 8500, 5500,
];
const DEFAULT_TOTAL = DEFAULT_DURATIONS.reduce((a, b) => a + b, 0);

export interface PipelineLoopProps {
  /**
   * Total loop length in ms — scales the default stage ratios. The
   * landing page runs it compressed (~14000); /compass runs the full
   * ~20000-24000. Ignored when `durations` is provided.
   */
  cycleMs?: number;
  /** Explicit per-stage durations in ms (Analyze, Challenge, Debate, Synthesize). */
  durations?: [number, number, number, number];
  /** Show the small "LOOPS EVERY ~Ns · PHASE" status line. Default true. */
  showStatusLine?: boolean;
  className?: string;
}

// ─── Content (approved sample-cycle data) ───────────────────────────────

type SigKind = "pos" | "neg" | "neu" | "cat";

const SIG_COLOR: Record<SigKind, string> = {
  pos: "#10B981",
  neg: "#EF4444",
  neu: "#64748B",
  cat: "#F59E0B",
};

interface AnalyzerDef {
  id:
    | "fundamental"
    | "valuation"
    | "smart_money"
    | "narrative"
    | "macro"
    | "technical"
    | "catalyst";
  name: string;
  sig: string;
  sigKind: SigKind;
  conf: string;
}

const ANALYZERS: AnalyzerDef[] = [
  { id: "fundamental", name: "Fundamental", sig: "+", sigKind: "pos", conf: "8.4" },
  { id: "valuation", name: "Valuation", sig: "−", sigKind: "neg", conf: "7.9" },
  { id: "smart_money", name: "Smart Money", sig: "−", sigKind: "neg", conf: "8.1" },
  { id: "narrative", name: "Narrative", sig: "=", sigKind: "neu", conf: "4.2" },
  { id: "macro", name: "Macro", sig: "−", sigKind: "neg", conf: "6.0" },
  { id: "technical", name: "Technical", sig: "−", sigKind: "neg", conf: "7.2" },
  { id: "catalyst", name: "Catalyst", sig: "!", sigKind: "cat", conf: "9.0" },
];

const STAMPS: Array<{ label: string; kind: "ok" | "flag" }> = [
  { label: "FUNDAMENTAL ✓", kind: "ok" },
  { label: "VALUATION ✓", kind: "ok" },
  { label: "SMART MONEY ✓", kind: "ok" },
  { label: "NARRATIVE ⚑ thin data", kind: "flag" },
  { label: "MACRO ⚑ weak link", kind: "flag" },
  { label: "TECHNICAL ✓", kind: "ok" },
  { label: "CATALYST ✓", kind: "ok" },
];

interface SeatDef {
  x: number;
  y: number;
  init: string;
  label: string;
  labelSide: "top" | "left" | "right";
}

const SEATS: SeatDef[] = [
  { x: 160, y: 45, init: "BU", label: "BULL", labelSide: "top" },
  { x: 255, y: 76, init: "BE", label: "BEAR", labelSide: "right" },
  { x: 255, y: 128, init: "VA", label: "VALUE", labelSide: "right" },
  { x: 65, y: 128, init: "GR", label: "GROWTH", labelSide: "left" },
  { x: 65, y: 76, init: "CO", label: "CONTRARIAN", labelSide: "left" },
];

const CONV_TARGET = [6.8, 8.2, 7.6, 6.1, 5.4];
const CONV_COLOR = ["#10B981", "#EF4444", "#EF4444", "#10B981", "#F59E0B"];
const SPEAK_SEQ = [1, 0, 2, 4, 3, 1, 0, 2, 4, 1];
const ARC_SEQ = [1, 0, 2, 3, 4, 1, 0, 2, 3, 0];
const ARCS = [
  "M160,45 Q195,108 255,76",
  "M255,76 Q160,118 65,128",
  "M255,128 Q195,95 160,45",
  "M65,76 Q160,128 255,76",
  "M65,128 Q130,108 160,45",
];
const CONV_CIRC = 2 * Math.PI * 17;

const TRANSCRIPT: Array<{ tag: string; tagClass: string; text: string }> = [
  {
    tag: "BEAR",
    tagClass: "bg-cred/15 text-[#F87171]",
    text: "six-executive cluster sell undercuts the quality story",
  },
  {
    tag: "BULL",
    tagClass: "bg-cgreen/15 text-cgreen-light",
    text: "capex converts to revenue from Q4; margins hold at 46%",
  },
  {
    tag: "VALUE",
    tagClass: "bg-blue/15 text-blue-glow",
    text: "P88 of the DCF distribution leaves no margin of safety",
  },
  {
    tag: "CONTRARIAN",
    tagClass: "bg-gold/15 text-gold-light",
    text: "consensus is crowded; the one cautious note stands out",
  },
];
const TRANSCRIPT_AT = [0.1, 0.27, 0.44, 0.61];

const STAGE_META = [
  {
    n: "STAGE 01",
    name: "Analyze",
    desc: "Seven lenses on the week's data, in parallel. Each reports a signal and its own confidence.",
    phase: "ANALYZE",
  },
  {
    n: "STAGE 02",
    name: "Challenge",
    desc: "An adversarial pass attacks every analyzer's reasoning before it's allowed to count.",
    phase: "CHALLENGE",
  },
  {
    n: "STAGE 03",
    name: "Debate",
    desc: "Five personas argue the evidence for ten rounds. A judge tracks which case survives.",
    phase: "DEBATE",
  },
  {
    n: "STAGE 04",
    name: "Synthesize",
    desc: "One writer reconciles seven signals, the red-team flags, and the debate verdict into your brief.",
    phase: "SYNTHESIZE",
  },
] as const;

// ─── Micro-SVG visuals (pure functions of analyzer-local progress) ──────

/** Draw-in path: uses pathLength so dash math is uniform across shapes. */
function DrawPath({
  d,
  stroke,
  strokeWidth,
  dash,
  p,
}: {
  d: string;
  stroke: string;
  strokeWidth: number;
  dash?: string;
  p: number; // 0..1 drawn fraction
}) {
  return (
    <path
      d={d}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={dash ?? "100"}
      strokeDashoffset={dash ? undefined : 100 * (1 - clamp01(p))}
      pathLength={100}
      style={
        dash
          ? { opacity: clamp01(p) > 0 ? 1 : 0, transition: `opacity 300ms ${EASE}` }
          : undefined
      }
    />
  );
}

function AnalyzerVisual({ id, ap }: { id: AnalyzerDef["id"]; ap: number }) {
  const e = easeOutCubic(clamp01(ap));
  switch (id) {
    case "fundamental": {
      // 4 bars growing from the baseline, staggered.
      const bars = [
        { x: 6, y: 10, h: 24, fill: "#3B82F6" },
        { x: 28, y: 4, h: 30, fill: "#3B82F6" },
        { x: 50, y: 14, h: 20, fill: "#3B82F6" },
        { x: 72, y: 7, h: 27, fill: "#10B981" },
      ];
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          {bars.map((b, k) => {
            const s = easeOutCubic(window01(ap, k * 0.14, 0.5));
            return (
              <rect
                key={k}
                x={b.x}
                y={b.y}
                width={14}
                height={b.h}
                rx={2}
                fill={b.fill}
                style={{ transform: `scaleY(${s})`, transformOrigin: "50% 34px", transformBox: "view-box" }}
              />
            );
          })}
        </svg>
      );
    }
    case "valuation":
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          <DrawPath
            d="M4,34 C20,33 30,28 42,18 C54,8 62,5 70,4 C80,3 90,8 96,16"
            stroke="#3B82F6"
            strokeWidth={1.6}
            p={window01(ap, 0, 0.7)}
          />
          <circle cx={78} cy={4.5} r={3.4} fill="#EF4444" style={{ opacity: ap > 0.75 ? 1 : 0, transition: `opacity 300ms ${EASE}` }} />
        </svg>
      );
    case "smart_money": {
      const blips = [
        { x: 8, y: 16, h: 9, hot: false },
        { x: 18, y: 12, h: 13, hot: false },
        { x: 28, y: 18, h: 7, hot: false },
        { x: 40, y: 8, h: 17, hot: true },
        { x: 48, y: 6, h: 19, hot: true },
        { x: 56, y: 9, h: 16, hot: true },
        { x: 70, y: 15, h: 10, hot: false },
        { x: 82, y: 13, h: 12, hot: false },
      ];
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          {blips.map((b, k) => (
            <rect
              key={k}
              x={b.x}
              y={b.y}
              width={3}
              height={b.h}
              fill={b.hot ? "#EF4444" : "#64748B"}
              style={{ opacity: ap > 0.08 + k * 0.1 ? 1 : 0, transition: `opacity 300ms ${EASE}` }}
            />
          ))}
        </svg>
      );
    }
    case "narrative": {
      const rot = lerp(-50, 8, e);
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          <path d="M14,32 A 38 38 0 0 1 86,32" stroke="rgba(148,163,184,.25)" strokeWidth={2} fill="none" />
          <line
            x1={50}
            y1={32}
            x2={50}
            y2={8}
            stroke="#F59E0B"
            strokeWidth={1.8}
            style={{ transform: `rotate(${rot}deg)`, transformOrigin: "50px 32px", transformBox: "view-box" }}
          />
        </svg>
      );
    }
    case "macro":
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          <DrawPath
            d="M4,12 C24,10 40,14 58,20 C74,25 88,27 96,27"
            stroke="#3B82F6"
            strokeWidth={1.6}
            p={window01(ap, 0, 0.6)}
          />
          <DrawPath
            d="M4,26 C24,25 44,23 64,18 C78,15 90,12 96,10"
            stroke="rgba(148,163,184,.4)"
            strokeWidth={1.2}
            dash="3 3"
            p={window01(ap, 0.25, 0.5)}
          />
        </svg>
      );
    case "technical":
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          <DrawPath
            d="M4,8 C20,10 34,14 48,20 C64,27 80,32 96,33"
            stroke="#EF4444"
            strokeWidth={1.6}
            p={window01(ap, 0, 0.6)}
          />
          <DrawPath
            d="M4,20 C28,19 52,19 96,21"
            stroke="rgba(148,163,184,.5)"
            strokeWidth={1.2}
            p={window01(ap, 0.2, 0.55)}
          />
          <circle cx={46} cy={19.5} r={3.2} fill="#F59E0B" style={{ opacity: ap > 0.8 ? 1 : 0, transition: `opacity 300ms ${EASE}` }} />
        </svg>
      );
    case "catalyst":
      return (
        <svg viewBox="0 0 100 38" aria-hidden className="mb-[7px] mt-1 block h-[38px] w-full">
          <g
            stroke="rgba(148,163,184,.2)"
            strokeWidth={1}
            fill="none"
            style={{ opacity: clamp01(ap / 0.4), transition: `opacity 300ms ${EASE}` }}
          >
            <rect x={10} y={6} width={80} height={26} rx={3} />
            <line x1={10} y1={14} x2={90} y2={14} />
            <line x1={30} y1={14} x2={30} y2={32} />
            <line x1={50} y1={14} x2={50} y2={32} />
            <line x1={70} y1={14} x2={70} y2={32} />
          </g>
          <circle cx={60} cy={23} r={4} fill="#F59E0B" style={{ opacity: ap > 0.6 ? 1 : 0, transition: `opacity 300ms ${EASE}` }} />
        </svg>
      );
  }
}

// ─── Stage panels ────────────────────────────────────────────────────────

function AnalyzerCard({ def, ap }: { def: AnalyzerDef; ap: number }) {
  const running = ap > 0 && ap < 1;
  const done = ap >= 0.9;
  return (
    <div
      className="rounded-[10px] border bg-[#0A101F] px-[9px] pb-[9px] pt-[10px]"
      style={{
        borderColor: running ? "rgba(59,130,246,.55)" : done ? LINE_STRONG : LINE,
        boxShadow: running ? "0 0 18px -6px rgba(59,130,246,.4)" : "none",
        transition: `border-color 400ms ${EASE}, box-shadow 400ms ${EASE}`,
      }}
    >
      <div
        className="h-6 text-[0.6rem] uppercase leading-[1.3] tracking-[0.06em] text-[#94A3B8]"
        style={{ fontFamily: MONO }}
      >
        {def.name}
      </div>
      <AnalyzerVisual id={def.id} ap={ap} />
      <div
        className="flex h-4 items-center justify-between text-[0.66rem]"
        style={{ fontFamily: MONO }}
      >
        <span
          className="font-medium"
          style={{
            color: SIG_COLOR[def.sigKind],
            opacity: done ? 1 : 0,
            transform: done ? "scale(1)" : "scale(.6)",
            transition: `all 350ms ${EASE}`,
          }}
        >
          {def.sig}
        </span>
        <span
          className="font-tabular text-[#64748B]"
          style={{ opacity: done ? 1 : 0, transition: `opacity 350ms ${EASE}` }}
        >
          {def.conf}
        </span>
      </div>
    </div>
  );
}

function DebatePanel({ sp }: { sp: number }) {
  const ROUNDS_END = 0.78;
  const roundFloat = sp <= 0 ? 0 : (Math.min(sp, ROUNDS_END) / ROUNDS_END) * 10;
  const round = sp <= 0 ? 0 : Math.min(10, Math.floor(roundFloat) + (sp >= ROUNDS_END ? 0 : 1));
  const roundShown = sp >= ROUNDS_END ? 10 : round;
  const roundLocal = roundFloat - Math.floor(roundFloat);
  const debating = sp > 0.02 && sp < ROUNDS_END;
  const speaking = debating && round >= 1 ? SPEAK_SEQ[round - 1] : -1;
  const litArc = debating && round >= 1 && roundLocal < 0.7 ? ARC_SEQ[round - 1] : -1;
  const verdictT = easeOutCubic(window01(sp, 0.85, 0.08));
  const liveLine =
    sp < 0.74
      ? TRANSCRIPT_AT.reduce((acc, at, i) => (sp >= at ? i : acc), -1)
      : -1;

  return (
    <div
      className="relative grid items-center gap-5 overflow-hidden rounded-xl border bg-[#0A101F] p-[18px] md:grid-cols-[340px_1fr]"
      style={{ borderColor: LINE }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(60% 90% at 26% 50%, rgba(59,130,246,.07), transparent 70%)" }}
      />
      {/* Boardroom table */}
      <div className="relative">
        <svg viewBox="0 0 320 200" aria-hidden className="block w-full">
          <defs>
            <radialGradient id="plTblG" cx="50%" cy="40%" r="70%">
              <stop offset="0%" stopColor="rgba(59,130,246,.10)" />
              <stop offset="55%" stopColor="rgba(30,41,59,.55)" />
              <stop offset="100%" stopColor="rgba(10,16,31,.9)" />
            </radialGradient>
          </defs>
          <ellipse cx={160} cy={100} rx={112} ry={56} fill="url(#plTblG)" stroke="rgba(148,163,184,.35)" strokeWidth={1} />
          <ellipse cx={160} cy={100} rx={97} ry={45} fill="none" stroke="rgba(148,163,184,.1)" />
          {ARCS.map((d, i) => (
            <path
              key={i}
              d={d}
              fill="none"
              strokeWidth={1.1}
              stroke={litArc === i ? "rgba(245,158,11,.35)" : "rgba(245,158,11,0)"}
              style={{ transition: `stroke 300ms ${EASE}` }}
            />
          ))}
          <text x={160} y={98} textAnchor="middle" className="font-tabular" style={{ fontFamily: MONO, fontSize: 13, fill: "#F59E0B", letterSpacing: "1.5px" }}>
            ROUND {roundShown}
          </text>
          <text x={160} y={112} textAnchor="middle" style={{ fontFamily: MONO, fontSize: 7.5, fill: "#64748B", letterSpacing: "2px" }}>
            OF 10 · JUDGE SCORING
          </text>
          {SEATS.map((s, i) => {
            const speak = speaking === i;
            const v = round >= 1 ? 5 + (CONV_TARGET[i] - 5) * (roundShown / 10) : 0;
            const convOffset = v > 0 ? CONV_CIRC * (1 - v / 10) : CONV_CIRC;
            const convStroke = roundShown > 5 ? CONV_COLOR[i] : "rgba(148,163,184,.4)";
            const rippleR = 14 + 13 * roundLocal;
            const rippleO = speak ? 0.7 * (1 - roundLocal) : 0;
            return (
              <g key={s.init} transform={`translate(${s.x},${s.y})`}>
                <circle r={rippleR} fill="none" stroke="#F59E0B" strokeWidth={1.2} opacity={rippleO} />
                <circle
                  r={17}
                  fill="none"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  stroke={convStroke}
                  strokeDasharray={CONV_CIRC}
                  strokeDashoffset={convOffset}
                  style={{ transform: "rotate(-90deg)", transformBox: "fill-box", transformOrigin: "center", transition: `stroke 500ms ${EASE}` }}
                />
                <circle
                  r={13}
                  fill={speak ? "rgba(245,158,11,.25)" : "#1E293B"}
                  stroke={speak ? "#F59E0B" : "rgba(148,163,184,.45)"}
                  strokeWidth={1}
                  style={{ transition: `fill 300ms ${EASE}, stroke 300ms ${EASE}` }}
                />
                <text y={3} textAnchor="middle" style={{ fontFamily: MONO, fontSize: 9, fill: speak ? "#F59E0B" : "#F1F5F9", letterSpacing: ".5px" }}>
                  {s.init}
                </text>
                <text
                  x={s.labelSide === "right" ? 26 : s.labelSide === "left" ? -26 : 0}
                  y={s.labelSide === "top" ? -24 : 3}
                  textAnchor={s.labelSide === "right" ? "start" : s.labelSide === "left" ? "end" : "middle"}
                  style={{ fontFamily: MONO, fontSize: 8.5, fill: "#94A3B8", letterSpacing: ".8px" }}
                >
                  {s.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {/* Transcript */}
      <div className="min-w-0">
        <div
          className="mb-3 flex justify-between text-[0.7rem] tracking-[0.12em] text-[#64748B]"
          style={{ fontFamily: MONO }}
        >
          <span>DEBATE TRANSCRIPT</span>
          <b className="font-medium text-gold">26 LLM CALLS</b>
        </div>
        {TRANSCRIPT.map((t, i) => {
          const shown = sp >= TRANSCRIPT_AT[i];
          const live = liveLine === i;
          return (
            <div
              key={t.tag}
              className="mb-1.5 truncate rounded-r-md border-l-2 px-2.5 py-[7px] text-[0.72rem] leading-[1.5]"
              style={{
                fontFamily: MONO,
                borderLeftColor: live ? "#F59E0B" : "transparent",
                background: live ? "rgba(30,41,59,.6)" : "transparent",
                color: live ? "#94A3B8" : "#64748B",
                opacity: shown ? (live ? 1 : 0.6) : 0,
                transform: shown ? "none" : "translateY(6px)",
                transition: `all 400ms ${EASE}`,
              }}
            >
              <span className={cn("mr-2 inline-block rounded px-1.5 py-px text-[0.6rem] font-medium tracking-[0.08em]", t.tagClass)}>
                {t.tag}
              </span>
              {t.text}
            </div>
          );
        })}
        <div
          className="relative mt-2.5 inline-block overflow-hidden rounded-lg border px-3.5 py-[9px] text-[0.7rem] tracking-[0.06em] text-gold"
          style={{
            fontFamily: MONO,
            borderColor: "rgba(245,158,11,.55)",
            opacity: verdictT,
            transform: verdictT >= 1 ? "none" : "translateY(6px)",
            transition: `all 500ms ${EASE}`,
          }}
        >
          JUDGE · QUALITY CASE STRONG, PRICE CASE STRONGER · CONF 7/10
        </div>
      </div>
    </div>
  );
}

function SynthesizePanel({ sp }: { sp: number }) {
  const busy = sp > 0 && sp < 0.45;
  const sent = sp >= 0.45;
  const showT = easeOutCubic(window01(sp, 0.45, 0.18));
  const ringT = easeOutCubic(window01(sp, 0.5, 0.3));
  const conviction = 7 * ringT;
  const spin = sp * 1080; // pure function of progress — pauses with the driver
  return (
    <div className="grid items-center gap-5 md:grid-cols-[170px_1fr]">
      {/* Writer core */}
      <div className="relative mx-auto h-[150px] w-[150px]">
        <div
          className="absolute inset-0 rounded-full border"
          style={{
            borderColor: LINE_STRONG,
            borderTopColor: busy ? "#F59E0B" : LINE_STRONG,
            transform: `rotate(${spin}deg)`,
          }}
        />
        <div
          className="absolute inset-[10px] rounded-full border opacity-50"
          style={{
            borderColor: LINE_STRONG,
            borderTopColor: busy ? "#F59E0B" : LINE_STRONG,
            transform: `rotate(${-spin * 0.7}deg)`,
          }}
        />
        <div
          className="absolute inset-[26px] grid place-items-center rounded-full border text-center text-[0.6rem] leading-[1.5] tracking-[0.1em] text-gold"
          style={{
            fontFamily: MONO,
            borderColor: "rgba(245,158,11,.4)",
            background: "radial-gradient(circle at 38% 32%, rgba(245,158,11,.3), rgba(245,158,11,.06))",
          }}
        >
          {sent ? "BRIEF SENT" : busy ? (
            <span>
              RECON·
              <br />
              CILING
            </span>
          ) : (
            "IDLE"
          )}
        </div>
      </div>
      {/* Brief card */}
      <div
        className="rounded-xl border bg-[#16203A] px-5 py-[18px]"
        style={{
          borderColor: LINE,
          opacity: showT,
          transform: `translateY(${10 * (1 - showT)}px)`,
        }}
      >
        <div className="mb-2.5 flex items-center gap-3.5">
          <div className="text-[0.95rem]" style={{ fontFamily: MONO }}>
            MSFT <span className="text-[0.7rem] text-[#64748B]">NASDAQ</span>
          </div>
          <div className="ml-auto flex items-center gap-[9px]">
            <svg viewBox="0 0 42 42" aria-hidden className="h-[42px] w-[42px] -rotate-90">
              <circle cx={21} cy={21} r={18} fill="none" strokeWidth={4} stroke="rgba(148,163,184,.15)" />
              <circle
                cx={21}
                cy={21}
                r={18}
                fill="none"
                strokeWidth={4}
                strokeLinecap="round"
                stroke="#F59E0B"
                strokeDasharray={113}
                strokeDashoffset={113 * (1 - conviction / 10)}
              />
            </svg>
            <div className="font-tabular text-[1.15rem]" style={{ fontFamily: SERIF }}>
              {conviction.toFixed(1)}
            </div>
          </div>
        </div>
        <p className="text-[1.05rem] leading-[1.5] text-[#F1F5F9]" style={{ fontFamily: SERIF }}>
          An exceptional business at a <em className="italic text-gold">demanding price.</em>{" "}
          Shares sit at the 88th percentile of our DCF distribution; the median is{" "}
          <span className="font-tabular">$353</span>. Elite fundamentals, no margin of safety
          at current levels.
        </p>
        <p className="mt-2.5 text-[0.68rem] text-[#64748B]">
          Scores and evidence, traced to source. Educational research, not investment advice.
        </p>
      </div>
    </div>
  );
}

// ─── Connector (gold packet drops to the next stage) ────────────────────

function Connector({ nextSp }: { nextSp: number }) {
  const t = window01(nextSp, 0, 0.07);
  const flowing = t > 0 && t < 1;
  return (
    <div
      className="relative ml-5 h-[34px] w-0.5 md:ml-[46px]"
      style={{ background: `linear-gradient(${LINE_STRONG}, transparent)` }}
      aria-hidden
    >
      <span
        className="absolute -left-[3px] h-2 w-2 rounded-full bg-gold"
        style={{
          top: lerp(-4, 30, easeOutCubic(t)),
          opacity: flowing ? 1 - t : 0,
          boxShadow: "0 0 10px #F59E0B",
        }}
      />
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────

export function PipelineLoop({
  cycleMs,
  durations,
  showStatusLine = true,
  className,
}: PipelineLoopProps) {
  const stageDurations = useMemo<number[]>(() => {
    if (durations) return [...durations];
    if (cycleMs && cycleMs > 0) {
      return DEFAULT_DURATIONS.map((d) => (d * cycleMs) / DEFAULT_TOTAL);
    }
    return [...DEFAULT_DURATIONS];
  }, [durations, cycleMs]);

  const driver = useStageDriver<HTMLDivElement>({
    durations: stageDurations,
    reactStateFps: 30,
  });
  const { frame, reducedMotion } = driver;
  const sp = (i: number) => stageLocalProgress(frame, i);
  const totalSec = Math.round(stageDurations.reduce((a, b) => a + b, 0) / 1000);
  const phase = reducedMotion ? "COMPLETE" : STAGE_META[frame.stage]?.phase ?? "ANALYZE";

  /** A stage section dims until the loop reaches it (all-on under reduced motion). */
  const stageOn = (i: number) => reducedMotion || frame.stage >= i;

  const railButton = (i: number) => (
    <button
      type="button"
      onClick={() => driver.goToStage(i)}
      aria-label={`Jump to ${STAGE_META[i].name} (stage ${i + 1} of 4)`}
      aria-current={frame.stage === i ? "step" : undefined}
      className="group block w-full rounded-md pt-1.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-glow"
    >
      <div
        className="text-[0.72rem] tracking-[0.12em] text-[#64748B] group-hover:text-[#94A3B8]"
        style={{ fontFamily: MONO, transition: `color 150ms ${EASE}` }}
      >
        {STAGE_META[i].n}
      </div>
      <div className="mt-1 text-[1.15rem]" style={{ fontFamily: SERIF }}>
        {STAGE_META[i].name}
      </div>
      <div className="mt-1.5 text-[0.78rem] leading-[1.45] text-[#64748B]">
        {STAGE_META[i].desc}
      </div>
    </button>
  );

  return (
    <div
      ref={driver.ref}
      className={cn("text-[#F1F5F9]", className)}
      aria-label="Animated replay of one Compass research cycle: analyze, challenge, debate, synthesize"
    >
      {showStatusLine && (
        <p
          className="mb-9 font-tabular text-[0.75rem] tracking-[0.08em] text-[#64748B]"
          style={{ fontFamily: MONO }}
          aria-live="off"
        >
          LOOPS EVERY ~{totalSec}s · <span className="text-[#94A3B8]">{phase}</span>
        </p>
      )}

      {/* STAGE 1 — ANALYZE */}
      <section
        className="grid gap-2.5 md:grid-cols-[120px_1fr] md:gap-6"
        style={{ opacity: stageOn(0) ? 1 : 0.32, transition: `opacity 500ms ${EASE}` }}
      >
        <div>{railButton(0)}</div>
        <div className="grid grid-cols-4 gap-2 md:grid-cols-7">
          {ANALYZERS.map((a, i) => (
            <AnalyzerCard key={a.id} def={a} ap={window01(sp(0), 0.05 + i * 0.09, 0.42)} />
          ))}
        </div>
      </section>
      <Connector nextSp={sp(1)} />

      {/* STAGE 2 — CHALLENGE */}
      <section
        className="grid gap-2.5 md:grid-cols-[120px_1fr] md:gap-6"
        style={{ opacity: stageOn(1) ? 1 : 0.32, transition: `opacity 500ms ${EASE}` }}
      >
        <div>{railButton(1)}</div>
        <div
          className="flex flex-wrap items-center gap-2 rounded-xl border bg-[#0A101F] px-4 py-3.5"
          style={{ borderColor: LINE }}
        >
          <span
            className="mr-1.5 text-[0.66rem] tracking-[0.1em] text-[#64748B]"
            style={{ fontFamily: MONO }}
          >
            RED&nbsp;TEAM
          </span>
          {STAMPS.map((s, i) => {
            const active = sp(1) >= 0.08 + i * 0.12;
            return (
              <span
                key={s.label}
                className="rounded-full border px-2.5 py-1 text-[0.66rem]"
                style={{
                  fontFamily: MONO,
                  opacity: active ? 1 : 0.35,
                  transform: active ? "scale(1)" : "scale(.92)",
                  borderColor: active
                    ? s.kind === "ok"
                      ? "rgba(16,185,129,.5)"
                      : "rgba(245,158,11,.6)"
                    : LINE,
                  color: active ? (s.kind === "ok" ? "#10B981" : "#F59E0B") : "#64748B",
                  transition: `all 300ms ${EASE}`,
                }}
              >
                {s.label}
              </span>
            );
          })}
        </div>
      </section>
      <Connector nextSp={sp(2)} />

      {/* STAGE 3 — DEBATE */}
      <section
        className="grid gap-2.5 md:grid-cols-[120px_1fr] md:gap-6"
        style={{ opacity: stageOn(2) ? 1 : 0.32, transition: `opacity 500ms ${EASE}` }}
      >
        <div>{railButton(2)}</div>
        <DebatePanel sp={sp(2)} />
      </section>
      <Connector nextSp={sp(3)} />

      {/* STAGE 4 — SYNTHESIZE */}
      <section
        className="grid gap-2.5 md:grid-cols-[120px_1fr] md:gap-6"
        style={{ opacity: stageOn(3) ? 1 : 0.32, transition: `opacity 500ms ${EASE}` }}
      >
        <div>{railButton(3)}</div>
        <SynthesizePanel sp={sp(3)} />
      </section>
    </div>
  );
}
